import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import {
  DuplicateAIAgentData,
  aiAgentJourneys,
  createAIAgent,
  duplicateAIAgent,
  loadAIAgents,
  removeAIAgent,
  testAIAgent,
  updateAIAgent,
} from './requests';

const aiAgentQueryKey = ['ai-agents'];

const useInvalidateAIAgents = () => {
  const queryClient = useQueryClient();
  const invalidateAIAgents = () => queryClient.invalidateQueries(aiAgentQueryKey);

  return { invalidateAIAgents };
};

export const useAIAgents = (options: { staleTime?: number; keepPreviousData?: boolean } = {}) => {
  return useQuery({
    queryKey: ['ai-agents'],
    queryFn: loadAIAgents,
    ...options,
  });
};

export const useCreateAIAgent = () => {
  const { invalidateAIAgents } = useInvalidateAIAgents();

  return useMutation({
    mutationKey: ['ai-agent-create'],
    mutationFn: createAIAgent,
    onSuccess: () => {
      invalidateAIAgents();
    },
  });
};

export const useUpdateAIAgent = () => {
  const { invalidateAIAgents } = useInvalidateAIAgents();

  return useMutation({
    mutationKey: ['ai-agent-update'],
    mutationFn: updateAIAgent,
    onSuccess: () => {
      invalidateAIAgents();
    },
  });
};

export const useDuplicateAIAgent = (shortId: string) => {
  const { invalidateAIAgents } = useInvalidateAIAgents();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationKey: ['ai-agent-duplicate'],
    mutationFn: (data: DuplicateAIAgentData) => duplicateAIAgent(shortId, data),
    onSuccess: () => {
      invalidateAIAgents();
      showSuccess(t('agentDuplicated'));
    },
  });
};

export const useRemoveAIAgent = () => {
  const { invalidateAIAgents } = useInvalidateAIAgents();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationKey: ['ai-agent-remove'],
    mutationFn: removeAIAgent,
    onSuccess: () => {
      invalidateAIAgents();
      showSuccess(t('agentRemoved'));
    },
  });
};

export const useTestAIAgent = () => {
  return useMutation({
    mutationKey: ['ai-agent-test'],
    mutationFn: testAIAgent,
  });
};

const locale = {
  en: {
    agentDuplicated: 'Agent duplicated',
    agentRemoved: 'Agent removed',
  },
};

export const useAIAgentJourneys = (shortId: string) => {
  return useQuery({
    queryKey: ['ai-agent-journeys', shortId],
    queryFn: () => aiAgentJourneys(shortId),
    enabled: Boolean(shortId),
  });
};
